import { CommandItem } from "@/components/Command/types";
import { getSoftDeletePresentation } from "@/lib/file-unavailable";
import { Document, File, Template } from "@/types/types";
import { MENTION_REGEX } from "@nnr/regex";
import { useEffect } from "react";
import { FileInfo } from "./folder";

type Marker =
  | "file"
  | "section"
  | "document"
  | "range"
  | "template"
  | "library"
  | "skill";

export interface MentionInfo {
  type: Marker;
  id: string;
  raw: string;
}

export const parseMentions = (text: string): MentionInfo[] => {
  const mentions: MentionInfo[] = [];
  let match;

  const regex = new RegExp(MENTION_REGEX);

  while ((match = regex.exec(text)) !== null) {
    const [raw, type, id] = match;
    mentions.push({
      type: type as Marker,
      id,
      raw,
    });
  }

  return mentions;
};

const parseSectionMentions = (text: string): string[] => {
  return parseMentions(text)
    .filter((mention) => mention.type === "section")
    .map((mention) => mention.id);
};

export const replaceMentions = (
  text: string,
  replacer: (mention: MentionInfo) => string,
): string => {
  return text.replace(MENTION_REGEX, (match, type, id) => {
    const mention: MentionInfo = {
      type: type as Marker,
      id,
      raw: match,
    };
    return replacer(mention);
  });
};

export const useMentionSections = (
  prompt: string,
  setSelectedSections: (sections: string[]) => void,
) => {
  const updateSelectedSections = () => {
    const sectionMentions = parseSectionMentions(prompt);
    setSelectedSections(sectionMentions);
  };

  useEffect(() => {
    updateSelectedSections();
  }, [prompt]);

  return { updateSelectedSections };
};

// ============================================================
// Tiptap contents
// ============================================================

/** Converts Tiptap signal to content */
export const mentionSignalToContent = (signal: {
  id: string;
  label: string;
}) => {
  return {
    type: "mention",
    attrs: {
      id: signal.id,
      label: signal.label,
    },
  };
};

/** Converts Tiptap range signal to content */
export const mentionRangeSignalToContent = (signal: {
  from: number;
  to: number;
}) => {
  return {
    type: "mention",
    attrs: {
      id: `[range:${signal.from}-${signal.to}]`,
      label: `${signal.from}...${signal.to}`,
    },
  };
};

function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function renderMentionSpan(params: {
  displayName: string;
  title: string;
  unavailable: boolean;
}): string {
  const className = params.unavailable
    ? "mention mention-unavailable"
    : "mention";
  return `<span class="${className}" title="${escapeHtmlAttr(params.title)}">${escapeHtmlAttr(params.displayName)}</span>`;
}

/** Replaces tiptap mentions with HTML */
export const renderTiptapMention = ({
  mention,
  files,
  documents,
  templates,
  libraryFiles,
  skills,
}: {
  mention: MentionInfo;
  files: FileInfo[];
  documents: Document[];
  templates: Template[];
  libraryFiles?: File[];
  skills?: { uuid: string; name: string }[];
}) => {
  if (mention.type === "file") {
    const file = files.find((f) => f.uuid === mention.id);
    if (!file) return "";
    const softDelete = getSoftDeletePresentation(
      file.dateDeleted,
      file.deletedReason,
    );
    const displayName = softDelete
      ? `${file.name} (${softDelete.shortLabel})`
      : file.name;
    const title =
      softDelete?.message ??
      (file.folderPath ? `${file.folderPath}/${file.name}` : file.name);
    return renderMentionSpan({
      displayName,
      title,
      unavailable: softDelete != null,
    });
  }

  if (mention.type === "range") {
    return `<span class="mention">Reference (${mention.id})</span>`;
  }

  if (mention.type === "document") {
    const name = documents.find((d) => d.uuid === mention.id)?.name;
    return name ? `<span class="mention">${escapeHtmlAttr(name)}</span>` : "";
  }

  if (mention.type === "template") {
    const name = templates.find((t) => t.uuid === mention.id)?.name;
    return name ? `<span class="mention">${escapeHtmlAttr(name)}</span>` : "";
  }

  if (mention.type === "library") {
    const libraryFile = libraryFiles?.find((f) => f.uuid === mention.id);
    if (!libraryFile) return "";
    const softDelete = getSoftDeletePresentation(
      libraryFile.dateDeleted,
      libraryFile.deletedReason,
    );
    const displayName = softDelete
      ? `${libraryFile.name} (${softDelete.shortLabel})`
      : libraryFile.name;
    return renderMentionSpan({
      displayName,
      title: softDelete?.message ?? libraryFile.name,
      unavailable: softDelete != null,
    });
  }

  if (mention.type === "skill") {
    const name = skills?.find((s) => s.uuid === mention.id)?.name;
    return name ? `<span class="mention">${escapeHtmlAttr(name)}</span>` : "";
  }

  return mention.raw;
};

// ============================================================
// Convert artifacts into mention objects for command panel
// ============================================================

export const fileToMentionCommand = ({
  file,
}: {
  file: FileInfo & { scopePrefix?: string | null };
}): CommandItem => {
  const softDelete = getSoftDeletePresentation(
    file.dateDeleted,
    file.deletedReason,
  );
  const prefix = file.scopePrefix ?? file.groupName ?? null;
  const base = prefix
    ? `${prefix} / ${file.folderPath} / ${file.name}`
    : `${file.folderPath} / ${file.name}`;
  return {
    id: `[file:${file.uuid}]`,
    artifactUuid: file.uuid,
    label: softDelete ? `${base} (${softDelete.shortLabel})` : base,
    type: "file",
    unavailable: softDelete?.message ?? null,
  };
};

export const documentToMentionCommand = ({
  document,
}: {
  document: Document;
}): CommandItem => {
  return {
    id: `[document:${document.uuid}]`,
    artifactUuid: document.uuid,
    label: document.name,
    type: "document",
  };
};

export const templateToMentionCommand = ({
  template,
}: {
  template: Pick<Template, "uuid" | "name" | "group">;
}): CommandItem => {
  const prefix = template.group?.name || null;
  return {
    id: `[template:${template.uuid}]`,
    artifactUuid: template.uuid,
    label: `${prefix ? `${prefix} / ` : ""}${template.name}`,
    type: "template",
  };
};

export const libraryToMentionCommand = ({
  file,
}: {
  file: File;
}): CommandItem => {
  const softDelete = getSoftDeletePresentation(
    file.dateDeleted,
    file.deletedReason,
  );
  return {
    id: `[library:${file.uuid}]`,
    artifactUuid: file.uuid,
    label: softDelete ? `${file.name} (${softDelete.shortLabel})` : file.name,
    type: "library",
    unavailable: softDelete?.message ?? null,
  };
};

export const skillToMentionCommand = ({
  skill,
}: {
  skill: {
    uuid: string;
    name: string;
    group?: { name: string } | null;
    scopePrefix?: string | null;
  };
}): CommandItem => {
  const prefix = skill.scopePrefix ?? skill.group?.name ?? null;
  return {
    id: `[skill:${skill.uuid}]`,
    artifactUuid: skill.uuid,
    label: `${prefix ? `${prefix} / ` : ""}${skill.name}`,
    type: "skill",
  };
};
